// ============================================================
// HabitPack Theme System - Types (V4)
// Themes are single-skin presets.
// ============================================================

/** Categories for organizing themes in the shop (V4) */
export type ThemeCategory =
  | 'system-core'
  | 'minimal-atmosphere'
  | 'organic-world'
  | 'retro-memory'
  | 'neon-cyber'
  | 'urban-expression'
  | 'fantasy-realms'
  | 'cosmic-scale'
  | 'event-realities'
  | 'premium-status'
  | 'alternate-modes'

/** Theme tier for complexity classification */
export type ThemeTier = 'common' | 'rare' | 'epic' | 'legendary'

/** Metadata for a theme category */
export interface ThemeCategoryMeta {
  id: ThemeCategory
  name: string
  description: string
  icon: string
}

/** All category metadata */
export const CATEGORY_META: ThemeCategoryMeta[] = [
  {
    id: 'system-core',
    name: 'System Core',
    description: 'Computacao crua, engenharia e precisao',
    icon: '🧩',
  },
  {
    id: 'minimal-atmosphere',
    name: 'Minimal Atmosphere',
    description: 'Silencio, foco e atmosfera clean',
    icon: '🕯️',
  },
  {
    id: 'neon-cyber',
    name: 'Neon & Cyber',
    description: 'Energia, lasers, CRT e codigo vivo',
    icon: '⚡',
  },
  {
    id: 'organic-world',
    name: 'Organic World',
    description: 'Terra, oceano, outono e neblina',
    icon: '🌿',
  },
  {
    id: 'retro-memory',
    name: 'Retro Memory',
    description: 'Cassette, polaroid, pixel e nostalgia',
    icon: '📼',
  },
  {
    id: 'urban-expression',
    name: 'Urban Expression',
    description: 'Atitude, rua e spray',
    icon: '🧱',
  },
  {
    id: 'fantasy-realms',
    name: 'Fantasy Realms',
    description: 'Realms, petals e atmosferas oniricas',
    icon: '🌸',
  },
  {
    id: 'cosmic-scale',
    name: 'Cosmic Scale',
    description: 'Auroras, nebulas e vastidao espacial',
    icon: '🪐',
  },
  {
    id: 'event-realities',
    name: 'Event Realities',
    description: 'Sazonais, comemorativos e edicoes limitadas',
    icon: '🎟️',
  },
  {
    id: 'premium-status',
    name: 'Premium & Status',
    description: 'Luxo, metais frios e presenca premium',
    icon: '🏛️',
  },
  {
    id: 'alternate-modes',
    name: 'Alternate Modes',
    description: 'Modos especiais, edicoes e identidades raras',
    icon: '🗝️',
  },
]


/** Shape-related CSS variables */
export interface ShapeVars {
  '--radius': string
  '--border-w': string
  '--shadow-card': string
  '--shadow-card-active': string
  // Modal layout tokens
  '--modal-padding': string
  '--modal-gap': string
  '--modal-header-gap': string
  '--modal-footer-gap': string
  '--modal-header-pb': string
  '--modal-footer-pt': string
  '--modal-backdrop-blur'?: string
}

/** Color-related CSS variables */
export interface ColorVars {
  // Base colors
  '--background': string
  '--foreground': string
  '--card': string
  '--card-foreground': string
  '--popover': string
  '--popover-foreground': string
  // Accent colors
  '--primary': string
  '--primary-foreground': string
  '--secondary': string
  '--secondary-foreground': string
  '--muted': string
  '--muted-foreground': string
  '--accent': string
  '--accent-foreground': string
  '--destructive': string
  '--destructive-foreground': string
  // Borders and inputs
  '--border': string
  '--input': string
  '--ring': string
  // Game tokens
  '--success': string
  '--success-foreground': string
  '--xp': string
  '--xp-bg': string
  '--coin': string
  '--streak': string
  // Card effects
  '--card-glow': string
  '--card-border-active': string
  '--card-completed': string
  '--hold-fill': string
  // Navigation
  '--nav-bg': string
  '--nav-active': string
  // Rarity tokens
  '--rarity-common'?: string
  '--rarity-rare'?: string
  '--rarity-epic'?: string
  '--rarity-legendary'?: string
  '--rarity-secret'?: string
  '--premium'?: string

  // Alert tokens
  '--alert-info': string
  '--alert-info-foreground': string
  '--alert-success': string
  '--alert-success-foreground': string
  '--alert-warning': string
  '--alert-warning-foreground': string
  '--alert-error': string
  '--alert-error-foreground': string

  // Priority tokens (Task/Project semantic)
  '--priority-low': string
  '--priority-medium': string
  '--priority-high': string
  '--priority-critical': string

  // Modal specific color/effect tokens
  '--modal-overlay-bg': string
  '--modal-bg': string
  '--modal-border': string
  '--modal-shadow': string
  '--tint-primary-subtle': string
}

/** Base style properties for a theme */
export interface ThemeBaseStyle {
  /** Extra CSS class names for the app shell (fonts, theme-specific classes) */
  shellClasses: string
  /** Card-level CSS class override */
  cardClasses: string
  /** Shape-related CSS variables */
  shapeVars: ShapeVars
}

/** A complete theme preset (single skin) */
export interface ThemePreset {
  id: string
  name: string
  tagline: string
  description: string
  category: ThemeCategory
  tier: ThemeTier
  basePrice: number
  swatches: string[]
  baseStyle: ThemeBaseStyle
  colorVars: ColorVars

  /** Optional: group variants for loyalty discounts later */
  familyId?: string
  /** Optional: display name for variant in the family */
  variantName?: string

  /** 
   * Pre-calculated CSS variables including HSL components.
   * If present, these are used directly instead of calculating at runtime.
   */
  resolvedVars?: Record<string, string>
}

/** Lightweight theme metadata for shop lists */
export interface ThemeMeta {
  id: string
  name: string
  tagline: string
  category: ThemeCategory
  tier: ThemeTier
  basePrice: number
  swatches: string[]
  familyId?: string
  variantName?: string
}

/**
 * Convert a hex color (#RRGGBB or #RGB) to HSL component string (e.g. "187 94% 53%").
 * Non-hex values (rgba, color-mix, etc.) are returned as-is.
 */
function hexToHslComponents(value: string): string {
  // Only convert hex colors
  if (!value.startsWith('#')) return value

  let hex = value.slice(1)
  if (hex.length === 3) {
    hex = hex[0] + hex[0] + hex[1] + hex[1] + hex[2] + hex[2]
  }
  if (hex.length !== 6) return value

  const r = parseInt(hex.slice(0, 2), 16) / 255
  const g = parseInt(hex.slice(2, 4), 16) / 255
  const b = parseInt(hex.slice(4, 6), 16) / 255

  const max = Math.max(r, g, b)
  const min = Math.min(r, g, b)
  const l = (max + min) / 2

  if (max === min) {
    return `0 0% ${Math.round(l * 100)}%`
  }

  const d = max - min
  const s = l > 0.5 ? d / (2 - max - min) : d / (max + min)

  let h = 0
  if (max === r) h = ((g - b) / d + (g < b ? 6 : 0)) / 6
  else if (max === g) h = ((b - r) / d + 2) / 6
  else h = ((r - g) / d + 4) / 6

  return `${Math.round(h * 360)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`
}

/** CSS variable keys that are NOT colors (pass through as-is) */
const PASSTHROUGH_KEYS = new Set([
  '--shadow-card',
  '--shadow-card-active',
  '--radius',
  '--border-w',
  '--modal-padding',
  '--modal-gap',
  '--modal-header-gap',
  '--modal-footer-gap',
  '--modal-header-pb',
  '--modal-footer-pt',
  '--modal-backdrop-blur',
])

/** Keys that already contain non-hex values (rgba, color-mix) - set as-is, no HSL variant */
const COMPLEX_VALUE_KEYS = new Set([
  '--card-glow',
  '--card-border-active',
  '--modal-overlay-bg',
  '--modal-shadow',
  '--tint-primary-subtle',
])

/**
 * Get the complete CSS vars for a theme.
 * Returns BOTH the original hex values AND -hsl suffixed HSL component variants.
 * - Original vars (e.g. --primary: #22d3ee) for inline styles, SCSS effects, color-mix
 * - HSL vars (e.g. --primary-hsl: 187 94% 53%) for Tailwind opacity modifiers
 */
export function getThemeVars(theme: ThemePreset): Record<string, string> {
  if (theme.resolvedVars) {
    return theme.resolvedVars
  }

  const raw: Record<string, string> = {
    ...theme.baseStyle.shapeVars,
    ...theme.colorVars,
  }

  const result: Record<string, string> = {}

  if (!raw['--tint-primary-subtle']) {
    const primaryValue = raw['--primary']
    if (primaryValue) {
      if (primaryValue.startsWith('#')) {
        const primaryHsl = hexToHslComponents(primaryValue)
        if (primaryHsl && primaryHsl.split(' ').length === 3) {
          result['--tint-primary-subtle'] = `hsla(${primaryHsl} / 0.15)`
        }
      } else if (primaryValue.startsWith('oklch(')) {
        const match = primaryValue.match(/oklch\(([^)]+)\)/)
        if (match) {
          const components = match[1].split('/').map(c => c.trim())[0]
          result['--tint-primary-subtle'] = `oklch(${components} / 0.15)`
        }
      }
    }
  }

  for (const [key, value] of Object.entries(raw)) {
    // Always set the original value
    result[key] = value

    // For color keys, also set the HSL/OKLCH component variants
    if (!PASSTHROUGH_KEYS.has(key) && !COMPLEX_VALUE_KEYS.has(key)) {
      if (value.startsWith('#')) {
        const hsl = hexToHslComponents(value)
        if (hsl !== value) {
          result[`${key}-hsl`] = hsl
        }
        result[`${key}-oklch`] = `from ${value} l c h`
      } else if (value.startsWith('oklch(')) {
        // Extract components between parentheses
        const match = value.match(/oklch\(([^)]+)\)/)
        if (match) {
          const components = match[1].split('/').map(c => c.trim())[0]
          result[`${key}-oklch`] = components
          // We can't easily convert to HSL here without a library, 
          // so we'll just omit -hsl or put something safely failing.
          // But for now, let's keep -oklch as the primary engine.
        }
      }
    }
  }
  return result
}
